import type { ButtonProps } from '@mui/material/Button';

import { useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useMutation } from '@apollo/client/react';

import Button from '@mui/material/Button';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { dispatch } from 'src/state';
import { CONFIG } from 'src/global-config';
import { useTranslate } from 'src/locales';
import { OAUTH_CONFIG } from 'src/config/oauth';
import * as ActionTypes from 'src/state/action-types';
import { LOGOUT_MUTATION, type LogoutMutationData } from 'src/graphql/user';

import { toast } from 'src/components/snackbar';

import { signOut as jwtSignOut } from 'src/auth/context/jwt/action';
import { signOut as amplifySignOut } from 'src/auth/context/amplify/action';
import { signOut as supabaseSignOut } from 'src/auth/context/supabase/action';
import { signOut as firebaseSignOut } from 'src/auth/context/firebase/action';

// ----------------------------------------------------------------------

const signOut =
  (CONFIG.auth.method === 'supabase' && supabaseSignOut) ||
  (CONFIG.auth.method === 'firebase' && firebaseSignOut) ||
  (CONFIG.auth.method === 'amplify' && amplifySignOut) ||
  jwtSignOut;

type Props = ButtonProps & {
  onClose?: () => void;
};

export function SignOutButton({ onClose, sx, ...other }: Props) {
  const router = useRouter();

  const { t } = useTranslate();

  const { logout: signOutAuth0 } = useAuth0();

  const [logoutMutation] = useMutation<LogoutMutationData>(LOGOUT_MUTATION);

  const clearAllCookies = () => {
    const cookies = document.cookie.split(';');

    cookies.forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();

      if (name) {
        // Clear cookie for current path
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;

        // Clear cookie for parent domain
        const { hostname } = window.location;
        if (hostname !== 'localhost') {
          // Try with current domain
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${hostname}`;
          // Try with dot-prefixed domain for subdomains
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${hostname}`;

          // Try with parent domain if it's a subdomain
          const parts = hostname.split('.');
          if (parts.length > 2) {
            const parentDomain = parts.slice(-2).join('.');
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${parentDomain}`;
          }
        } else {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=localhost`;
        }
      }
    });
  };

  const handleLogout = useCallback(async () => {
    try {
      // Dispatch Redux logout action to clear state
      dispatch({ type: ActionTypes.AUTH_LOGOUT_REQUEST });

      // Call the GraphQL logout mutation to clear cookies on the server
      // and revoke the refresh token at FusionAuth
      await logoutMutation();

      await signOut();

      // Clear all cookies on the client side as a fallback
      clearAllCookies();

      onClose?.();

      // Redirect through FusionAuth's logout endpoint so it clears its own
      // SSO session — without this the user gets silently re-authenticated
      // with the last-used identity provider on the next login.
      // post_logout_redirect_uri is intentionally omitted: FusionAuth only
      // allows pre-registered URIs and will reject unknown ones. The Logout
      // URL configured in the FusionAuth application settings handles the
      // redirect after the SSO session is cleared.
      const fusionAuthLogoutUrl = new URL(`${OAUTH_CONFIG.FUSIONAUTH_URL}/oauth2/logout`);
      fusionAuthLogoutUrl.searchParams.set('client_id', OAUTH_CONFIG.APPLICATION_ID);
      window.location.href = fusionAuthLogoutUrl.toString();
    } catch (error) {
      console.error(error);
      toast.error(t('Unable to logout!'));
    }
  }, [logoutMutation, onClose, t]);

  const handleLogoutAuth0 = useCallback(async () => {
    try {
      // Dispatch Redux logout action to clear state
      dispatch({ type: ActionTypes.AUTH_LOGOUT_REQUEST });

      // Call the GraphQL logout mutation to clear cookies on the server
      await logoutMutation();

      await signOutAuth0();

      // Clear all cookies on the client side as a fallback
      clearAllCookies();

      onClose?.();

      // Auth0 will handle the redirect after logout
      router.push(paths.auth.auth0.signIn);
    } catch (error) {
      console.error(error);
      toast.error(t('Unable to logout!'));
    }
  }, [logoutMutation, onClose, router, signOutAuth0, t]);

  return (
    <Button
      fullWidth
      variant="soft"
      size="large"
      color="error"
      onClick={CONFIG.auth.method === 'auth0' ? handleLogoutAuth0 : handleLogout}
      sx={sx}
      {...other}
    >
      {t('Logout')}
    </Button>
  );
}
